/**
 * Calls: voice and video, up to four people, over the sealed channel.
 *
 * The server relays encrypted signals and, at most, TURN packets it cannot
 * read. Everything that decides anything happens here, between the members.
 *
 * The mesh has one rule that keeps four people from producing glare: the
 * NEWCOMER offers. Whoever accepts a call sends an offer to every member
 * already in it, and the members answer. Two people never offer to each other
 * simultaneously because "already in" is decided by the order accepts arrive
 * in — and for the 1:1 case it degenerates to callee-offers-caller, which is
 * the classic shape.
 *
 * Membership is tracked by accepts seen, not by a roster message: the caller
 * is in from the start, every accept adds one, every hangup removes one. When
 * the set has only me left, the call is over.
 */
import type { VavClient } from '@vav/client-core';
import type { CallSignal } from '@vav/protocol';
import { translate } from '@vav/i18n';
import { closeCallNotification, notifyCall } from './notify.ts';

export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'active';

export interface CallPeer {
  userId: string;
  stream?: MediaStream;
  connected: boolean;
  /** The leg gave up: ICE exhausted every path it had. */
  failed?: boolean;
}

export interface CallState {
  phase: CallPhase;
  callId?: string;
  conversationId?: string;
  media: 'audio' | 'video';
  /** Who rang, for the incoming screen. */
  from?: string;
  peers: CallPeer[];
  localStream?: MediaStream;
  muted: boolean;
  cameraOff: boolean;
  startedAt?: number;
  /**
   * Whether the server could mint TURN credentials for this call. False means
   * STUN only: same-network calls connect, phone-to-phone across carriers
   * mostly will not — and the UI should say which failure this is.
   */
  relay?: boolean;
  /** Why the last call ended, shown briefly. */
  ended?: 'declined' | 'busy' | 'hangup' | 'failed' | 'unanswered';
}

const RING_TIMEOUT = 45_000;

const IDLE: CallState = { phase: 'idle', media: 'audio', peers: [], muted: false, cameraOff: false };

/**
 * The Answer button on the lock-screen notification cannot answer by itself —
 * the sealed accept has to come from a running client. So the button carries
 * an intent: the app opens (or focuses), calls `requestAnswer()`, and either
 * a call already ringing is answered on the spot or the next ring to arrive
 * within a minute is. Every live manager is in the registry, so a call to a
 * background profile answers exactly the same way.
 */
const managers = new Set<CallManager>();
let answerWantedUntil = 0;

export function requestAnswer(): void {
  for (const manager of managers) {
    if (manager.state.phase === 'incoming') {
      void manager.accept();
      return;
    }
  }
  answerWantedUntil = Date.now() + 60_000;
}

function consumePendingAnswer(): boolean {
  if (Date.now() >= answerWantedUntil) return false;
  answerWantedUntil = 0;
  return true;
}

export class CallManager {
  #client: VavClient;
  #state: CallState = { ...IDLE };
  #listeners = new Set<() => void>();
  #connections = new Map<string, RTCPeerConnection>();
  /** ICE candidates that arrived before the connection existed. */
  #pendingIce = new Map<string, RTCIceCandidateInit[]>();
  #ringTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * While ringing out, the ring signal repeats every few seconds. Each repeat
   * becomes a fresh push on a phone whose app is frozen, and `renotify` on
   * the worker's notification turns the repeats into a ring cadence — the
   * closest a PWA gets to ringing a phone that is in a pocket.
   */
  #reRing: ReturnType<typeof setInterval> | undefined;
  #unsubscribe: (() => void) | undefined;

  constructor(client: VavClient) {
    this.#client = client;
    this.#unsubscribe = client.on('call-signal', (event) =>
      void this.#onSignal(event).catch(() => undefined),
    );
    managers.add(this);
  }

  dispose(): void {
    managers.delete(this);
    this.#unsubscribe?.();
    this.#teardown();
  }

  get state(): CallState {
    return this.#state;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #set(patch: Partial<CallState>): void {
    this.#state = { ...this.#state, ...patch };
    for (const listener of this.#listeners) listener();
  }

  // ---------------------------------------------------------------- outgoing

  async start(conversationId: string, media: 'audio' | 'video', ringee?: string): Promise<void> {
    if (this.#state.phase !== 'idle') return;
    const callId = crypto.randomUUID();
    let local: MediaStream;
    try {
      local = await navigator.mediaDevices.getUserMedia({ audio: true, video: media === 'video' });
    } catch {
      this.#set({ ...IDLE, ended: 'failed' });
      return;
    }
    this.#set({
      phase: 'outgoing',
      callId,
      conversationId,
      media,
      // In a DM the person being rung is known before they answer; showing
      // them is what makes the outgoing screen readable. In a group the list
      // fills as accepts arrive.
      peers: ringee ? [{ userId: ringee, connected: false }] : [],
      localStream: local,
      muted: false,
      cameraOff: false,
      ended: undefined,
    });
    try {
      await this.#client.sendCallSignal(conversationId, callId, { type: 'ring', media });
    } catch {
      this.#endLocally('failed');
      return;
    }
    // Every four seconds, so the receiving phone's notification re-alerts at
    // something like a ring cadence; each repeat is a fresh push over there.
    this.#reRing = setInterval(() => {
      if (this.#state.phase !== 'outgoing' || this.#state.callId !== callId) return;
      void this.#client.sendCallSignal(conversationId, callId, { type: 'ring', media }).catch(() => undefined);
    }, 4_000);
    this.#ringTimer = setTimeout(() => {
      if (this.#state.phase === 'outgoing') void this.hangup('unanswered');
    }, RING_TIMEOUT);
  }

  // ---------------------------------------------------------------- incoming

  async accept(): Promise<void> {
    const { phase, callId, conversationId, media, from } = this.#state;
    if (phase !== 'incoming' || !callId || !conversationId || !from) return;
    clearTimeout(this.#ringTimer);
    let local: MediaStream;
    try {
      local = await navigator.mediaDevices.getUserMedia({ audio: true, video: media === 'video' });
    } catch {
      void this.#client.sendCallSignal(conversationId, callId, { type: 'decline' });
      this.#set({ ...IDLE, ended: 'failed' });
      return;
    }
    closeCallNotification();
    this.#set({ phase: 'active', localStream: local, startedAt: Date.now() });
    await this.#client.sendCallSignal(conversationId, callId, { type: 'accept' });
    // The newcomer offers: one leg to everyone already in. On accept the only
    // members I know for certain are the caller and anyone whose accept I saw
    // while ringing — both are in `peers` by now.
    for (const peer of this.#state.peers) {
      await this.#offerTo(peer.userId);
    }
  }

  async decline(): Promise<void> {
    const { callId, conversationId } = this.#state;
    if (callId && conversationId) {
      try {
        await this.#client.sendCallSignal(conversationId, callId, { type: 'decline' });
      } catch {
        // Declining into the void still ends it locally.
      }
    }
    this.#endLocally(undefined);
  }

  // ------------------------------------------------------------------ active

  async hangup(reason: CallState['ended'] = 'hangup'): Promise<void> {
    const { callId, conversationId } = this.#state;
    if (callId && conversationId) {
      try {
        await this.#client.sendCallSignal(conversationId, callId, { type: 'hangup' });
      } catch {
        // Nothing to do: the timeout on the other side cleans up.
      }
    }
    this.#endLocally(reason);
  }

  toggleMute(): void {
    const stream = this.#state.localStream;
    if (!stream) return;
    const muted = !this.#state.muted;
    for (const track of stream.getAudioTracks()) track.enabled = !muted;
    this.#set({ muted });
  }

  toggleCamera(): void {
    const stream = this.#state.localStream;
    if (!stream) return;
    const cameraOff = !this.#state.cameraOff;
    for (const track of stream.getVideoTracks()) track.enabled = !cameraOff;
    this.#set({ cameraOff });
  }

  /** Clear the "call ended" note once the UI has shown it. */
  dismissEnded(): void {
    if (this.#state.phase === 'idle') this.#set({ ended: undefined });
  }

  // ------------------------------------------------------------------- signals

  async #onSignal(event: {
    conversationId: string;
    from: string;
    callId: string;
    signal: CallSignal;
  }): Promise<void> {
    const { conversationId, from, callId, signal } = event;
    const mine = this.#state.callId === callId;

    switch (signal.type) {
      case 'ring': {
        if (this.#state.phase !== 'idle') {
          // Already busy with something; one call at a time.
          if (!mine) {
            void this.#client.sendCallSignal(conversationId, callId, { type: 'busy' }, from);
          }
          return;
        }
        this.#set({
          phase: 'incoming',
          callId,
          conversationId,
          media: signal.media,
          from,
          // The caller is a member from the start.
          peers: [{ userId: from, connected: false }],
          muted: false,
          cameraOff: false,
          ended: undefined,
        });
        // Wake the phone: the overlay only rings for someone already looking.
        notifyCall(
          this.#client.getContact(from)?.displayName ?? from,
          translate(signal.media === 'video' ? 'call.incoming.video' : 'call.incoming.audio'),
          translate('call.accept'),
          translate('call.decline'),
        );
        this.#ringTimer = setTimeout(() => {
          if (this.#state.phase === 'incoming') this.#endLocally(undefined);
        }, RING_TIMEOUT);
        // The lock-screen Answer button got here first: honour it.
        if (consumePendingAnswer()) void this.accept();
        return;
      }

      case 'accept': {
        if (!mine) return;
        if (!this.#state.peers.some((p) => p.userId === from)) {
          this.#set({ peers: [...this.#state.peers, { userId: from, connected: false }] });
        }
        if (this.#state.phase === 'outgoing') {
          // First accept: the call is on. The accepter will offer to us.
          clearTimeout(this.#ringTimer);
          clearInterval(this.#reRing);
          this.#set({ phase: 'active', startedAt: Date.now() });
          return;
        }
        if (this.#state.phase === 'active' && !this.#connections.has(from)) {
          // Two people accepted so close together that neither saw the other
          // before offering: nobody offers this leg by the newcomer rule. Give
          // the ordinary path two seconds, then the lower user id offers —
          // deterministic on both ends, so exactly one side does.
          const myId = this.#client.account?.userId ?? '';
          setTimeout(() => {
            if (
              this.#state.phase === 'active' &&
              this.#state.callId === callId &&
              !this.#connections.has(from) &&
              myId < from
            ) {
              void this.#offerTo(from).catch(() => undefined);
            }
          }, 2000);
        }
        return;
      }

      case 'offer': {
        if (!mine || this.#state.phase !== 'active') return;
        if (!this.#state.peers.some((p) => p.userId === from)) {
          this.#set({ peers: [...this.#state.peers, { userId: from, connected: false }] });
        }
        const connection = await this.#connectionFor(from);
        await connection.setRemoteDescription({ type: 'offer', sdp: signal.sdp });
        await this.#drainIce(from, connection);
        const answer = await connection.createAnswer();
        await connection.setLocalDescription(answer);
        await this.#client.sendCallSignal(
          this.#state.conversationId!,
          callId,
          { type: 'answer', sdp: answer.sdp ?? '' },
          from,
        );
        return;
      }

      case 'answer': {
        if (!mine) return;
        const connection = this.#connections.get(from);
        if (!connection) return;
        await connection.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
        await this.#drainIce(from, connection);
        return;
      }

      case 'ice': {
        if (!mine) return;
        const candidate: RTCIceCandidateInit = {
          candidate: signal.candidate,
          sdpMid: signal.sdpMid,
          sdpMLineIndex: signal.sdpMLineIndex,
        };
        const connection = this.#connections.get(from);
        // Before the remote description is set, candidates must wait.
        if (connection?.remoteDescription) {
          await connection.addIceCandidate(candidate).catch(() => undefined);
        } else {
          const pending = this.#pendingIce.get(from) ?? [];
          pending.push(candidate);
          this.#pendingIce.set(from, pending);
        }
        return;
      }

      case 'decline':
      case 'busy': {
        if (!mine) return;
        // In a 1:1 that ends the attempt. In a group, one refusal only matters
        // if nobody else is in yet.
        if (this.#state.phase === 'outgoing') {
          this.#endLocally(signal.type === 'busy' ? 'busy' : 'declined');
        }
        return;
      }

      case 'hangup': {
        if (!mine) return;
        this.#dropPeer(from);
        return;
      }
    }
  }

  // -------------------------------------------------------------------- mesh

  async #offerTo(userId: string): Promise<void> {
    const connection = await this.#connectionFor(userId);
    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    await this.#client.sendCallSignal(
      this.#state.conversationId!,
      this.#state.callId!,
      { type: 'offer', sdp: offer.sdp ?? '' },
      userId,
    );
  }

  async #connectionFor(userId: string): Promise<RTCPeerConnection> {
    const existing = this.#connections.get(userId);
    if (existing) return existing;

    let iceServers: RTCIceServer[] = [{ urls: 'stun:stun.cloudflare.com:3478' }];
    try {
      const minted = await this.#client.fetchIceServers();
      iceServers = minted.iceServers as RTCIceServer[];
      if (this.#state.relay !== minted.relay) this.#set({ relay: minted.relay });
    } catch {
      // STUN only: degraded, not dead.
      if (this.#state.relay === undefined) this.#set({ relay: false });
    }

    const connection = new RTCPeerConnection({ iceServers });
    this.#connections.set(userId, connection);

    const local = this.#state.localStream;
    if (local) for (const track of local.getTracks()) connection.addTrack(track, local);

    connection.onicecandidate = ({ candidate }) => {
      if (!candidate || !this.#state.callId) return;
      void this.#client.sendCallSignal(
        this.#state.conversationId!,
        this.#state.callId,
        {
          type: 'ice',
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid ?? undefined,
          sdpMLineIndex: candidate.sdpMLineIndex ?? undefined,
        },
        userId,
      );
    };

    connection.ontrack = ({ streams }) => {
      const stream = streams[0];
      if (!stream) return;
      this.#set({
        peers: this.#state.peers.map((p) =>
          p.userId === userId ? { ...p, stream, connected: true } : p,
        ),
      });
    };

    connection.onconnectionstatechange = () => {
      const state = connection.connectionState;
      if (state === 'failed') {
        // Leave the tile up with its verdict instead of silently dropping:
        // "failed" with a reason is diagnosable from a screenshot, an empty
        // call is not. The row names TURN when TURN is what is missing.
        this.#set({
          peers: this.#state.peers.map((p) =>
            p.userId === userId ? { ...p, connected: false, failed: true } : p,
          ),
        });
        return;
      }
      if (state === 'closed') this.#dropPeer(userId);
      if (state === 'connected') {
        this.#set({
          peers: this.#state.peers.map((p) => (p.userId === userId ? { ...p, connected: true } : p)),
        });
      }
    };

    return connection;
  }

  async #drainIce(userId: string, connection: RTCPeerConnection): Promise<void> {
    const pending = this.#pendingIce.get(userId);
    if (!pending) return;
    this.#pendingIce.delete(userId);
    for (const candidate of pending) {
      await connection.addIceCandidate(candidate).catch(() => undefined);
    }
  }

  #dropPeer(userId: string): void {
    this.#connections.get(userId)?.close();
    this.#connections.delete(userId);
    this.#pendingIce.delete(userId);
    const peers = this.#state.peers.filter((p) => p.userId !== userId);

    if (this.#state.phase === 'incoming' && this.#state.from === userId) {
      // The caller gave up while we were still ringing.
      this.#endLocally(undefined);
      return;
    }
    if (this.#state.phase === 'active' && peers.length === 0) {
      this.#endLocally('hangup');
      return;
    }
    if (this.#state.phase === 'outgoing' && peers.length === 0) {
      this.#endLocally(undefined);
      return;
    }
    this.#set({ peers });
  }

  #endLocally(reason: CallState['ended']): void {
    closeCallNotification();
    this.#teardown();
    this.#set({ ...IDLE, ended: reason });
  }

  #teardown(): void {
    clearTimeout(this.#ringTimer);
    clearInterval(this.#reRing);
    for (const connection of this.#connections.values()) connection.close();
    this.#connections.clear();
    this.#pendingIce.clear();
    for (const track of this.#state.localStream?.getTracks() ?? []) track.stop();
  }
}
